import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Search, Shield, Users, Calendar, Mail, Globe, FileText, Send, Copy, BarChart3, ChevronDown, ChevronRight, Plus, Minus, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import AdminNotificationSender from '@/components/AdminNotificationSender';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  confirmed_at: string;
  is_sso_user: boolean;
  profile?: {
    organization_name: string;
    industry: string;
    country: string;
  };
  websites?: Array<{
    id: string;
    domain: string;
    title: string;
    created_at: string;
  }>;
}

const AdminDashboard = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [manualRequests, setManualRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [resultJson, setResultJson] = useState<string>('');
  const [selectedWebsiteDomain, setSelectedWebsiteDomain] = useState<string | null>(null);
  const [userUsage, setUserUsage] = useState<any[]>([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [selectedBenefit, setSelectedBenefit] = useState<{benefitId: string, userId: string, benefitType: string, currentCount: number} | null>(null);
  const [usageAdjustment, setUsageAdjustment] = useState<number>(0);
  const [selectedBenefitLimit, setSelectedBenefitLimit] = useState<{benefitId: string, benefitType: string, currentLimit: number | null} | null>(null);
  const [limitAdjustment, setLimitAdjustment] = useState<number | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is admin - SECURE CHECK
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            title: "Access Denied",
            description: "You must be logged in to access the admin dashboard.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        // Double-check admin status using RPC function
        const { data, error } = await supabase
          .rpc('is_admin', { user_uuid: user.id });

        if (error) {
          console.error('Error checking admin status:', error);
          toast({
            title: "Access Denied",
            description: "Failed to verify admin status.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        if (!data) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access the admin dashboard.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast({
          title: "Access Denied",
          description: "Failed to verify admin access.",
          variant: "destructive",
        });
        navigate('/');
      } finally {
        setAdminLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate, toast]);

  // Fetch users data
  useEffect(() => {
    if (!isAdmin) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('No session found');
        }

        // Call the admin edge function
        const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-get-users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch users');
        }

        const { users: fetchedUsers } = await response.json();
        setUsers(fetchedUsers || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: "Error",
          description: "Failed to fetch users data.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    fetchManualRequests();
    fetchUserUsage();
  }, [isAdmin, toast]);

  const fetchUserUsage = async () => {
    if (!isAdmin) return;
    try {
      setLoadingUsage(true);
      console.log('Fetching usage data...');
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      // Call the admin edge function to fetch usage data (bypasses RLS)
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-get-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch usage data' }));
        throw new Error(errorData.error || 'Failed to fetch usage data');
      }

      const { users } = await response.json();
      console.log('Fetched usage data:', users?.length || 0, 'users');
      setUserUsage(users || []);
    } catch (error) {
      console.error('Error fetching usage:', error);
      toast({
        title: "Error",
        description: `Failed to fetch usage data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
      setUserUsage([]);
    } finally {
      setLoadingUsage(false);
    }
  };

  const updateUserUsage = async (benefitId: string, newCount: number, benefitType: string) => {
    if (!isAdmin) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-update-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          benefitId,
          usedCount: Math.max(0, newCount),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update usage' }));
        throw new Error(errorData.error || 'Failed to update usage');
      }

      toast({
        title: "Success",
        description: `Updated ${benefitType} usage to ${newCount}.`,
      });
      fetchUserUsage();
    } catch (error) {
      console.error('Error updating usage:', error);
      toast({
        title: "Error",
        description: `Failed to update usage: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const updateBenefitLimit = async (benefitId: string, newLimit: number | null, benefitType: string) => {
    if (!isAdmin) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-update-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          benefitId,
          benefitLimit: newLimit === null || newLimit === undefined ? null : Math.max(0, newLimit),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update limit' }));
        throw new Error(errorData.error || 'Failed to update limit');
      }

      toast({
        title: "Success",
        description: `Updated ${benefitType} limit to ${newLimit === null ? 'unlimited' : newLimit}.`,
      });
      fetchUserUsage();
    } catch (error) {
      console.error('Error updating limit:', error);
      toast({
        title: "Error",
        description: `Failed to update limit: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const resetUserUsage = async (benefitId: string, benefitType: string) => {
    await updateUserUsage(benefitId, 0, benefitType);
  };

  const increaseUsage = async (benefitId: string, currentCount: number, benefitType: string, amount: number = 1) => {
    await updateUserUsage(benefitId, currentCount + amount, benefitType);
  };

  const decreaseUsage = async (benefitId: string, currentCount: number, benefitType: string, amount: number = 1) => {
    await updateUserUsage(benefitId, Math.max(0, currentCount - amount), benefitType);
  };

  const syncUsageForAll = async () => {
    if (!isAdmin) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      toast({
        title: "Syncing...",
        description: "Syncing usage data from actual platform usage...",
      });

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-sync-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to sync usage' }));
        throw new Error(errorData.error || 'Failed to sync usage');
      }

      const result = await response.json();
      toast({
        title: "Success",
        description: result.message || `Synced ${result.synced?.length || 0} benefit(s)`,
      });
      fetchUserUsage();
    } catch (error) {
      console.error('Error syncing usage:', error);
      toast({
        title: "Error",
        description: `Failed to sync usage: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const createBenefitsForUser = async (subscriptionId: string) => {
    if (!isAdmin || !subscriptionId) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-create-benefits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscriptionId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create benefits' }));
        throw new Error(errorData.error || 'Failed to create benefits');
      }

      const result = await response.json();
      toast({
        title: "Success",
        description: result.message || "Benefits created successfully.",
      });
      fetchUserUsage();
    } catch (error) {
      console.error('Error creating benefits:', error);
      toast({
        title: "Error",
        description: `Failed to create benefits: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const toggleUserExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const fetchManualRequests = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-manual-analysis`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'list_requests' })
      });
      const json = await res.json().catch(() => ({ success: false }));
      if (!res.ok || !json?.success) {
        console.warn('Admin list_requests returned non-success', json);
        // Fallback: query via client with RLS admin policy
        const { data, error } = await supabase
          .from('manual_analysis_requests')
          .select('id, status, requested_at, completed_at, website_id, user_id, admin_prompt, notes')
          .order('requested_at', { ascending: false });
        if (!error && data) setManualRequests(data);
        return;
      }
      setManualRequests(json.requests || []);
    } catch (e) {
      console.error('Failed to load manual requests', e);
      // Last resort fallback
      const { data } = await supabase
        .from('manual_analysis_requests')
        .select('id, status, requested_at, completed_at, website_id, user_id, admin_prompt, notes')
        .order('requested_at', { ascending: false });
      if (data) setManualRequests(data);
    }
  };

  const setProcessing = async (id: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await fetch(`${supabase.supabaseUrl}/functions/v1/admin-manual-analysis`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'set_processing', request_id: id })
    });
    fetchManualRequests();
  };

  const submitManualResult = async () => {
    if (!selectedRequest) return;
    let parsed: any;
    try { parsed = JSON.parse(resultJson); } catch { toast({ title: 'Invalid JSON', variant: 'destructive' }); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const res = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-manual-analysis`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'submit_result', request_id: selectedRequest.id, result: parsed })
    });
    const j = await res.json().catch(() => ({ success: false, error: 'Invalid response' }));
    if (res.ok && j.success) {
      toast({ title: 'Manual analysis saved successfully!' });
      setSelectedRequest(null);
      setResultJson('');
      fetchManualRequests();
    } else {
      toast({ title: 'Save failed', description: j.error || 'Unknown error', variant: 'destructive' });
    }
  };

  // Filter users based on search and status
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.profile?.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.profile?.industry?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'confirmed' && user.confirmed_at) ||
                         (filterStatus === 'unconfirmed' && !user.confirmed_at) ||
                         (filterStatus === 'sso' && user.is_sso_user);

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: users.length,
    confirmed: users.filter(u => u.confirmed_at).length,
    unconfirmed: users.filter(u => !u.confirmed_at).length,
    sso: users.filter(u => u.is_sso_user).length,
    withWebsites: users.filter(u => u.websites && u.websites.length > 0).length,
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Checking admin access...</span>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight flex items-center gap-3">
                <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
                  <Shield className="h-6 w-6" />
                </div>
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground text-lg font-medium mt-2">Manage platform users and monitor system activity</p>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-xl">
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Confirmed</CardTitle>
              <Mail className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
            </CardContent>
          </Card>

          <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unconfirmed</CardTitle>
              <Mail className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.unconfirmed}</div>
            </CardContent>
          </Card>

          <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">SSO Users</CardTitle>
              <Shield className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.sso}</div>
            </CardContent>
          </Card>

          <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">With Websites</CardTitle>
              <Globe className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.withWebsites}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content with Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="usage" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Usage
            </TabsTrigger>
            <TabsTrigger value="analysis" className="gap-2">
              <FileText className="h-4 w-4" />
              Manual Analysis
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Send className="h-4 w-4" />
              Notifications
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Filters */}
            <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email, organization, or industry..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 rounded-xl"
                      />
                    </div>
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-48 rounded-xl">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                      <SelectItem value="sso">SSO Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
              <CardHeader>
                <CardTitle>Users ({filteredUsers.length})</CardTitle>
                <CardDescription>
                  All registered users on the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Loading users...</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Websites</TableHead>
                          <TableHead>Joined</TableHead>
                          <TableHead>Last Sign In</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{user.email}</div>
                                <div className="text-sm text-muted-foreground">
                                  {user.profile?.industry || 'No industry set'}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {user.profile?.organization_name || 'No organization'}
                              </div>
                              {user.profile?.country && (
                                <Badge variant="secondary" className="text-xs mt-1">
                                  {user.profile.country}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {user.confirmed_at ? (
                                  <Badge variant="default" className="w-fit">
                                    Confirmed
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="w-fit">
                                    Unconfirmed
                                  </Badge>
                                )}
                                {user.is_sso_user && (
                                  <Badge variant="outline" className="w-fit">
                                    SSO
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {user.websites?.length || 0} website(s)
                              </div>
                              {user.websites && user.websites.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {user.websites[0]?.domain}
                                  {user.websites.length > 1 && ` +${user.websites.length - 1} more`}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(user.created_at).toLocaleTimeString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              {user.last_sign_in_at ? (
                                <div className="text-sm">
                                  {new Date(user.last_sign_in_at).toLocaleDateString()}
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">Never</div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {!loading && filteredUsers.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No users found matching your criteria.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usage Management Tab */}
          <TabsContent value="usage" className="space-y-4">
            <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Usage Management</CardTitle>
                    <CardDescription>
                      View and manage subscription benefit usage for all users. Click on a user to expand and see their benefits.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={syncUsageForAll}
                    variant="outline"
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Sync Usage from Platform
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingUsage ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Loading usage data...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {userUsage.map((userData) => {
                      const isExpanded = expandedUsers.has(userData.user_id);
                      const hasBenefits = userData.benefits && userData.benefits.length > 0;
                      const hasSubscription = userData.subscription !== null;
                      const planName = userData.subscription?.plan_name || 'No Plan';
                      const planStatus = userData.subscription?.status || 'none';
                      
                      return (
                        <Collapsible key={userData.user_id} open={isExpanded} onOpenChange={() => toggleUserExpanded(userData.user_id)}>
                          <div className="border rounded-xl p-4 bg-background/50 hover:bg-background/70 transition-colors">
                            <CollapsibleTrigger className="w-full">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 flex-1">
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <div className="text-left">
                                      <div className="font-medium text-base">{userData.email}</div>
                                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                                        {hasSubscription && (
                                          <Badge variant={planStatus === 'active' ? 'default' : planStatus === 'trialing' ? 'secondary' : planStatus === 'canceled' || planStatus === 'expired' ? 'destructive' : 'outline'} className="text-xs">
                                            {planName}
                                          </Badge>
                                        )}
                                        {hasBenefits && (
                                          <span className="text-muted-foreground">
                                            {userData.benefits.length} benefit{userData.benefits.length !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {hasSubscription && !hasBenefits && (
                                          <span className="text-muted-foreground italic">
                                            No benefits configured
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {hasBenefits && (
                                  <div className="flex items-center gap-4">
                                    {userData.benefits.map((benefit: any) => {
                                      const remaining = benefit.benefit_limit !== null 
                                        ? Math.max(0, benefit.benefit_limit - benefit.used_count)
                                        : '∞';
                                      const isAtLimit = benefit.benefit_limit !== null && benefit.used_count >= benefit.benefit_limit;
                                      return (
                                        <div key={benefit.id} className="text-right">
                                          <div className="text-xs text-muted-foreground capitalize">
                                            {benefit.benefit_type?.replace('_', ' ')}
                                          </div>
                                          <div className={`text-sm font-medium ${isAtLimit ? 'text-orange-600' : ''}`}>
                                            {benefit.used_count || 0} / {benefit.benefit_limit !== null ? benefit.benefit_limit : '∞'}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {!hasBenefits && hasSubscription && (
                                  <div className="text-sm text-muted-foreground italic">
                                    Click to configure benefits
                                  </div>
                                )}
                              </div>
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent>
                              <div className="mt-4 pt-4 border-t space-y-3">
                                {hasBenefits ? (
                                  userData.benefits.map((benefit: any) => {
                                    const remaining = benefit.benefit_limit !== null 
                                      ? Math.max(0, benefit.benefit_limit - benefit.used_count)
                                      : '∞';
                                    const isAtLimit = benefit.benefit_limit !== null && benefit.used_count >= benefit.benefit_limit;
                                    const usagePercent = benefit.benefit_limit !== null 
                                      ? Math.min(100, (benefit.used_count / benefit.benefit_limit) * 100)
                                      : 0;
                                    
                                    return (
                                      <div key={benefit.id} className="p-3 bg-background rounded-lg border">
                                        <div className="flex items-center justify-between mb-3">
                                          <div>
                                            <div className="font-medium capitalize text-sm">
                                              {benefit.benefit_type?.replace('_', ' ')}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                              Reset period: {benefit.reset_period || 'N/A'}
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <div className={`text-lg font-bold ${isAtLimit ? 'text-orange-600' : ''}`}>
                                              {benefit.used_count || 0}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              of {benefit.benefit_limit !== null ? benefit.benefit_limit : '∞'} used
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {benefit.benefit_limit !== null && (
                                          <div className="mb-3">
                                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                              <span>Usage</span>
                                              <span>{Math.round(usagePercent)}%</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-2">
                                              <div 
                                                className={`h-2 rounded-full transition-all ${
                                                  isAtLimit ? 'bg-orange-600' : usagePercent > 80 ? 'bg-yellow-500' : 'bg-primary'
                                                }`}
                                                style={{ width: `${Math.min(100, usagePercent)}%` }}
                                              />
                                            </div>
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => decreaseUsage(benefit.id, benefit.used_count || 0, benefit.benefit_type)}
                                            className="h-8"
                                          >
                                            <Minus className="h-3 w-3 mr-1" />
                                            Decrease
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => increaseUsage(benefit.id, benefit.used_count || 0, benefit.benefit_type)}
                                            className="h-8"
                                          >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Increase
                                          </Button>
                                          <Dialog>
                                            <DialogTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  setSelectedBenefit({
                                                    benefitId: benefit.id,
                                                    userId: userData.user_id,
                                                    benefitType: benefit.benefit_type,
                                                    currentCount: benefit.used_count || 0
                                                  });
                                                  setUsageAdjustment(benefit.used_count || 0);
                                                }}
                                                className="h-8"
                                              >
                                                Set Value
                                              </Button>
                                            </DialogTrigger>
                                            <DialogContent>
                                              <DialogHeader>
                                                <DialogTitle>Set Usage Value</DialogTitle>
                                                <DialogDescription>
                                                  Set the exact usage count for {benefit.benefit_type?.replace('_', ' ')}
                                                </DialogDescription>
                                              </DialogHeader>
                                              <div className="space-y-4 py-4">
                                                <div>
                                                  <label className="text-sm font-medium mb-2 block">New Usage Count</label>
                                                  <Input
                                                    type="number"
                                                    min="0"
                                                    value={usageAdjustment}
                                                    onChange={(e) => setUsageAdjustment(parseInt(e.target.value) || 0)}
                                                  />
                                                  <p className="text-xs text-muted-foreground mt-1">
                                                    Current: {benefit.used_count || 0} | Limit: {benefit.benefit_limit !== null ? benefit.benefit_limit : 'Unlimited'}
                                                  </p>
                                                </div>
                                                <div className="flex gap-2 justify-end">
                                                  <Button
                                                    variant="outline"
                                                    onClick={() => {
                                                      setSelectedBenefit(null);
                                                      setUsageAdjustment(0);
                                                    }}
                                                  >
                                                    Cancel
                                                  </Button>
                                                  <Button
                                                    onClick={() => {
                                                      if (selectedBenefit) {
                                                        updateUserUsage(selectedBenefit.benefitId, usageAdjustment, selectedBenefit.benefitType);
                                                        setSelectedBenefit(null);
                                                        setUsageAdjustment(0);
                                                      }
                                                    }}
                                                  >
                                                    Update
                                                  </Button>
                                                </div>
                                              </div>
                                            </DialogContent>
                                          </Dialog>
                                          <Dialog>
                                            <DialogTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  setSelectedBenefitLimit({
                                                    benefitId: benefit.id,
                                                    benefitType: benefit.benefit_type,
                                                    currentLimit: benefit.benefit_limit
                                                  });
                                                  setLimitAdjustment(benefit.benefit_limit);
                                                }}
                                                className="h-8"
                                              >
                                                Set Limit
                                              </Button>
                                            </DialogTrigger>
                                            <DialogContent>
                                              <DialogHeader>
                                                <DialogTitle>Set Benefit Limit</DialogTitle>
                                                <DialogDescription>
                                                  Set the limit for {benefit.benefit_type?.replace('_', ' ')}. Leave empty for unlimited.
                                                </DialogDescription>
                                              </DialogHeader>
                                              <div className="space-y-4 py-4">
                                                <div>
                                                  <label className="text-sm font-medium mb-2 block">New Limit</label>
                                                  <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="Leave empty for unlimited"
                                                    value={limitAdjustment === null ? '' : limitAdjustment}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      setLimitAdjustment(val === '' ? null : parseInt(val) || 0);
                                                    }}
                                                  />
                                                  <p className="text-xs text-muted-foreground mt-1">
                                                    Current: {benefit.benefit_limit !== null ? benefit.benefit_limit : 'Unlimited'} | Used: {benefit.used_count || 0}
                                                  </p>
                                                </div>
                                                <div className="flex gap-2 justify-end">
                                                  <Button
                                                    variant="outline"
                                                    onClick={() => {
                                                      setSelectedBenefitLimit(null);
                                                      setLimitAdjustment(null);
                                                    }}
                                                  >
                                                    Cancel
                                                  </Button>
                                                  <Button
                                                    onClick={() => {
                                                      if (selectedBenefitLimit) {
                                                        updateBenefitLimit(selectedBenefitLimit.benefitId, limitAdjustment, selectedBenefitLimit.benefitType);
                                                        setSelectedBenefitLimit(null);
                                                        setLimitAdjustment(null);
                                                      }
                                                    }}
                                                  >
                                                    Update
                                                  </Button>
                                                </div>
                                              </div>
                                            </DialogContent>
                                          </Dialog>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => resetUserUsage(benefit.id, benefit.benefit_type)}
                                            className="h-8"
                                          >
                                            <RotateCcw className="h-3 w-3 mr-1" />
                                            Reset
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : hasSubscription ? (
                                  <div className="p-4 bg-muted/50 rounded-lg border border-dashed">
                                    <div className="text-center space-y-3">
                                      <p className="text-sm font-medium">Subscription Active but No Benefits Configured</p>
                                      <p className="text-xs text-muted-foreground">
                                        This user has a {planName} subscription ({planStatus}) but no benefits have been set up yet.
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        Benefits are typically created automatically when a subscription is activated. 
                                        Click the button below to create default benefits for this user.
                                      </p>
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          if (userData.subscription?.id) {
                                            createBenefitsForUser(userData.subscription.id);
                                          }
                                        }}
                                        className="mt-2"
                                      >
                                        <Plus className="h-3 w-3 mr-2" />
                                        Create Default Benefits
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 bg-muted/50 rounded-lg border border-dashed">
                                    <div className="text-center space-y-2">
                                      <p className="text-sm font-medium">No Active Subscription</p>
                                      <p className="text-xs text-muted-foreground">
                                        This user does not have an active subscription.
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}

                {!loadingUsage && userUsage.length === 0 && (
                  <div className="text-center py-8">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No users found.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manual Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            <Card className="bg-background/95 backdrop-blur-lg border border-border/30 shadow-[0_3px_16px_rgba(0,0,0,0.04)] rounded-3xl">
              <CardHeader>
                <CardTitle>Manual Analysis Requests ({manualRequests.length})</CardTitle>
                <CardDescription>Queue of users requesting admin-run analysis</CardDescription>
              </CardHeader>
              <CardContent>
                {manualRequests.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No manual requests yet.</div>
                ) : (
                  <div className="space-y-4">
                    {manualRequests.map((r) => (
                      <div key={r.id} className="p-4 border rounded-xl flex flex-col gap-2 bg-background/50">
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <div className="font-medium">Website ID: {r.website_id}</div>
                            <div className="text-muted-foreground">Requested: {new Date(r.requested_at).toLocaleString()} • Status: {r.status}</div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setProcessing(r.id)}>Mark Processing</Button>
                            <Button size="sm" onClick={async () => { 
                              setSelectedRequest(r);
                              setResultJson('');
                              setSelectedWebsiteDomain(null);
                              try {
                                const { data } = await supabase.from('websites').select('domain').eq('id', r.website_id).single();
                                setSelectedWebsiteDomain(data?.domain || null);
                              } catch {}
                            }}>Open</Button>
                          </div>
                        </div>
                        {selectedRequest?.id === r.id && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-sm font-medium">Copy Prompt</div>
                                <Button size="icon" variant="ghost" onClick={() => {
                                  const websiteUrl = selectedWebsiteDomain ? `https://${selectedWebsiteDomain}` : `${r.website_id}`;
                                  const prompt = `([User Website]: ${websiteUrl}\n[Article Topics Clusters Count]: 10\n[Article Ideas Per Topic Count]: 6\nPrimary Objective:\nAnalyze ${websiteUrl} and develop a content strategy with 10 topic clusters, each containing 6 article ideas. Focus on the top 3-5 most matching competitors for analysis to inform better topic clusters and article ideas.\n\nAnalysis Requirements (Internal Use - Do Not Output):\n\nBusiness & Competitor Analysis:\n\nAnalyze ${websiteUrl}: Business model, value proposition, target audience, content audit, SEO assessment, brand positioning.\nIdentify top 3-5 competitors, analyze their content strategies, keyword rankings, backlinks, and gaps.\n\nMarket Research & Keyword Analysis:\n\nConduct keyword research for high-volume, low-competition opportunities.\nAnalyze search intent, trends, and seasonal opportunities.\n\nLLM Optimization & Question-Based Research:\n\nSearch Google for customer questions related to the business (e.g., "[business type] how to", "[industry] what is").\nAnalyze top 3-5 ranking articles for headlines, depth, angles, authority.\nMap articles to real customer questions.\nOptimize for AI visibility: Comprehensive coverage, clear structure, direct answers, authority signals.\n\nContent Strategy Development:\n\nGenerate 10 topic clusters covering core topics, trends, educational content, comparisons, etc.\nFor each cluster and article, use analysis to ensure high-quality, SEO-optimized ideas.\n\nOutput Format Requirements:\nProvide output in strict JSON structure only. No additional text.\n{\n  "topic_clusters": [\n    {\n      "cluster_name": "string",\n      "strategic_rationale": "string",\n      "monthly_search_volume": number,\n      "average_keyword_difficulty": number,\n      "articles": [\n        {\n          "headline": "string",\n          "primary_keyword": "string",\n          "secondary_keywords": ["string", "string", "string"],\n          "monthly_search_volume": number,\n          "average_keyword_difficulty": number,\n          "ranking_difficulty": "low|medium|high",\n          "content_score": number,\n          "word_count_target": "1500-3000",\n          "headings": ["H1: string", "H2: string (paragraph count: number)", "H3: string (under above H2, paragraph count: number)"],\n          "images": [\n            {"placement": "Hero Image - Top of Article", "prompt": "Detailed description..."}\n          ],\n          "inspired_by": {"title": "string", "link": "string"},\n          "readability_stats": ["string", "string", "string", "string", "string"],\n          "llm_optimization": {"target_questions": ["string", "string"], "ai_quotable_sections": ["string"], "llm_citation_potential": number}\n        }\n      ]\n    }\n  ]\n}`;
                                  navigator.clipboard.writeText(prompt);
                                  toast({ title: 'Prompt copied' });
                                }}><Copy className="h-4 w-4"/></Button>
                              </div>
                              <div className="text-xs text-muted-foreground">Copies the standardized prompt with website and fixed counts (10 clusters, 6 ideas each).</div>
                            </div>
                            <div>
                              <div className="text-sm font-medium mb-1">Paste JSON Result</div>
                              <Textarea value={resultJson} onChange={(e) => setResultJson(e.target.value)} placeholder="Paste strict JSON here" className="min-h-[200px] font-mono rounded-xl" />
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={submitManualResult}>Submit Result</Button>
                              <Button variant="outline" onClick={() => { setSelectedRequest(null); setResultJson(''); }}>Close</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <AdminNotificationSender users={users} />
          </TabsContent>
        </Tabs>

        {/* Security Notice */}
        <Alert className="mt-6 rounded-xl">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            This admin dashboard is restricted to authorized personnel only. All access is logged and monitored.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default AdminDashboard;
